/**
 * 이상한 방 - 카탈로그 엔트리: 좌석 · 침실 그룹
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 4.1절(그리드 해상도) · 4.2절(무변화 회전) · 8절(카탈로그 규격 요건)
 * 작성 계약: `src/catalog/entryTypes.ts` · 실측 출처: `public/assets/models.json`
 * 형태 판정 근거: `public/assets/catalog-preview.png` (196종 시각 색인)
 *
 * ## 이 그룹이 맡는 것
 *
 * 소파 · 의자 · 벤치 · 스툴 · 침대 · 베개 · 옷걸이. 담당 모델은 32종인데 엔트리는 16종이다.
 * 나머지 16종을 버린 이유가 이 파일의 실질적인 내용이며, 크게 두 갈래다.
 *
 * 1. **기존 44종이 이미 그 GLB를 쓰고 있다(9종).**
 *    `chair` · `chair-desk` · `bench-cushion-low` · `stool-bar` · `pillow` · `cabinet-bed-drawer` ·
 *    `coat-rack-standing` · `lounge-sofa` · `bed-double` 은 `meshFactory.ts` 가 각각
 *    의자 · 데스크체어 · 벤치 · 바스툴 · 쿠션 · 협탁 · 코트걸이 · 소파 · 침대로 이미 배선했다.
 *    같은 `modelId` 를 두 엔트리가 쓰면 **화면에 같은 물건이 두 이름으로 나타난다** - 실루엣 중복의 정의다.
 *
 * 2. **실루엣이 대표 엔트리와 겹친다(7종).** 개별 사유는 각 채택 엔트리 옆 주석에 적었다.
 *    이 그룹은 "앉는 물건"이라 변종이 유난히 많고(의자만 6종), 그래서 가장 공격적으로 쳐냈다.
 *
 * ## 색으로 갈리는 변종은 채택하지 않는다 - 이 그룹의 핵심 판단
 *
 * `chair` 와 `chair-cushion` 은 프레임이 같고 방석 유무로만 갈린다. `pillow` 와 `pillow-blue` 는
 * 점유 칸(2×2×1)까지 같고 색만 다르다. 이런 쌍을 둘 다 넣으면 안 되는 이유는 "비슷해서"가 아니다 -
 * **색은 이 게임의 변수다(recolor 동사).** 체인저가 `chair` 의 방석을 분홍으로 칠하는 순간
 * 그건 화면상 `chair-cushion` 과 구별되지 않고, 술래는 "색이 바뀐 의자"와 "다른 의자로 교체됨"을
 * 영원히 가를 수 없다. 색이 유일한 차이인 두 엔트리는 원칙 2를 구조적으로 위반한다.
 * 그래서 이 파일이 채택한 변종은 전부 **구조(다리 배치 · 등받이 유무 · 점유 칸)** 로 갈린다.
 *
 * ## sizeClass 배분 규칙
 *
 *  - `anchor` : 소파 · 침대. 벽에 등을 붙이고, 1인칭 수색의 시야 차단물 역할까지 한다.
 *  - `floor`  : 의자 · 스툴 · 안락의자 · 오토만 · 협탁. 방 한가운데 자유롭게 선다.
 *  - `surface`: 긴 쿠션. 침대·소파 상판 위에 올라간다.
 *  - `wall`   : 벽걸이 옷걸이. 복도의 유일한 벽면 소품이라 태그를 넉넉히 줬다.
 *
 * ## recolorSlot 배분 규칙
 *
 * 천이 있는 가구는 예외 없이 천 슬롯(`carpet` / `carpetBlue`)만 지목한다.
 * 소파를 전 슬롯 칠하면 다리의 나무색까지 같이 먹혀 **단색 덩어리**가 되고, 그 순간
 * 실루엣 판독이 무너져 8절의 "실루엣이 서로 구별될 것" 요건이 깨진다.
 * 천이 없는 원목 가구(아치의자 · 협탁 · 옷걸이)만 `wood` 를 지목한다.
 */

import type { CatalogEntryDraft } from '../entryTypes'
import { cellsFromMeters } from '../entryTypes'

/* ------------------------------------------------------------------------------------------------
 * 실측 크기 (public/assets/models.json 의 sizeMeters 전사)
 * ---------------------------------------------------------------------------------------------- */

/**
 * 채택한 16종의 실측 바운딩 크기(m). **매니페스트에서 그대로 옮긴 값이며 손 추정치가 하나도 없다.**
 *
 * 여기 적힌 미터값을 그대로 두고 셀 변환을 `cellsFromMeters()` 에 맡기는 이유는
 * `entryTypes.ts` 가 이미 설명한 그대로다 - 스케일이 반영된 실측값을 손으로 다시 나누면
 * y(높이)와 z(깊이)를 바꿔 적는 사고가 반드시 나고, 그 결과는 눕혀진 모델이 아니라 **점유 칸 오류**다.
 *
 * 축 순서는 `Cell3` 규약을 따른다 - **y가 높이**, x·z가 바닥 평면.
 */
const MEASURED_SIZE_METERS = {
  'chair-rounded': { x: 0.4, y: 0.91, z: 0.4 },
  'chair-modern-frame-cushion': { x: 0.4, y: 0.92, z: 0.4 },
  'bench-cushion': { x: 0.8, y: 0.92, z: 0.4 },
  'stool-bar-square': { x: 0.307, y: 0.81, z: 0.295 },
  'lounge-chair': { x: 0.98, y: 0.92, z: 0.82 },
  'lounge-chair-relax': { x: 0.98, y: 1.26, z: 1.349 },
  'lounge-design-chair': { x: 1.459, y: 0.8, z: 0.82 },
  'lounge-sofa-ottoman': { x: 0.88, y: 0.46, z: 0.9 },
  'lounge-sofa-long': { x: 1.96, y: 0.92, z: 1.64 },
  'lounge-sofa-corner': { x: 1.96, y: 0.92, z: 1.96 },
  'lounge-design-sofa': { x: 2.24, y: 0.8, z: 0.82 },
  'bed-single': { x: 1.142, y: 0.75, z: 2.25 },
  'bed-bunk': { x: 1.142, y: 1.7, z: 2.19 },
  'cabinet-bed-drawer-table': { x: 0.532, y: 0.526, z: 0.435 },
  'pillow-long': { x: 0.773, y: 0.444, z: 0.177 },
  'coat-rack': { x: 0.896, y: 0.56, z: 0.269 },
} as const

/** 위 표에 실측이 등록된 모델만 이 파일에서 엔트리로 만들 수 있다. */
type SeatingModelId = keyof typeof MEASURED_SIZE_METERS

/**
 * `footprint` 를 뺀 엔트리 초안. footprint는 손으로 적는 필드가 아니라 **modelId에서 파생되는 값**이므로
 * 아예 입력에서 제거했다. 둘을 나란히 적게 두면 언젠가 한쪽만 고쳐져 어긋난다.
 */
type SeatingDraftSpec = Omit<CatalogEntryDraft, 'footprint' | 'modelId'> &
  Readonly<{ modelId: SeatingModelId }>

/** 실측 표에서 점유 칸을 파생시켜 엔트리를 완성한다. modelId ↔ footprint 불일치를 타입으로 막는 장치다. */
function draft(spec: SeatingDraftSpec): CatalogEntryDraft {
  return { ...spec, footprint: cellsFromMeters(MEASURED_SIZE_METERS[spec.modelId]) }
}

/* ------------------------------------------------------------------------------------------------
 * 엔트리 목록
 * ---------------------------------------------------------------------------------------------- */

/**
 * 좌석 · 침실 그룹 16종.
 *
 * `weight` 분포의 의도: 식탁의자 · 긴 쿠션 · 벽걸이 옷걸이처럼 실제 집에 여러 개 있는 물건을 높게(6~7),
 * 리클라이너 · 이층침대 · 코너소파처럼 집에 하나 있을까 말까 한 물건을 낮게(2~3) 뒀다.
 * 종 수를 늘려도 가중치가 평평하면 방에 코너소파가 다섯 개 놓인다 - 밀도는 종 수가 아니라 이 분포가 만든다.
 */
export const SEATING_BEDROOM_ENTRIES: readonly CatalogEntryDraft[] = [
  /* --- 의자: 기존 `chair`(나무 사다리등) · `chair-desk`(사무의자)와 구조가 갈리는 두 종만 --------- */

  // 아치형 등받이가 통짜 곡면이라 기존 나무 의자(수평 슬랫 2단)와 정면 실루엣이 확연히 갈린다.
  // rot 1: 등받이가 앞뒤를 가른다. 바닥 면적이 정사각(2×2)이어도 네 방향이 전부 구별된다.
  // recolor wood: 천이 없는 통 원목이라 지목할 색면이 프레임뿐이다.
  draft({
    id: 'chair-rounded',
    modelId: 'chair-rounded',
    name: '아치의자',
    rotSymmetry: 1,
    sizeClass: 'floor',
    recolorSlot: 'wood',
    tags: ['kitchen', 'living', 'bedroom'],
    weight: 7,
    defaultColorId: 5,
  }),

  // 캔틸레버(뒷다리 없이 썰매형 프레임이 앞으로 이어지는) 구조. 196종 전체에서 유일한 실루엣이라
  // 어느 방향에서 봐도 다른 의자와 헷갈리지 않는다.
  // rot 1: 등받이 + 뒷다리 부재가 앞뒤·좌우를 전부 가른다.
  // recolor carpetBlue: 방석만 칠해 금속 프레임의 밝은 선을 남긴다 - 이 선이 캔틸레버를 읽게 하는 요소다.
  //
  // 형제 모델 `chair-modern-cushion`(4다리 + 등판 채움)은 채택하지 않았다.
  // 같은 금속·같은 방석에 다리 구성만 평범해져서, 결국 기존 `chair` 의 금속 쌍둥이로 읽힌다.
  draft({
    id: 'chair-cantilever',
    modelId: 'chair-modern-frame-cushion',
    name: '캔틸레버 의자',
    rotSymmetry: 1,
    sizeClass: 'floor',
    recolorSlot: 'carpetBlue',
    tags: ['kitchen', 'living'],
    weight: 5,
    defaultColorId: 2,
  }),

  // 2인용 등받이 벤치. 기존 `bench`(모델 bench-cushion-low)는 등받이가 없는 높이 2칸 좌대라
  // 이쪽은 높이가 4칸이고 등받이가 있어 옆 실루엣이 완전히 다르다.
  // rot 1: 등받이가 한쪽에만 있다.
  // recolor carpet: 방석만. 프레임 원목을 남겨야 '벤치'로 읽힌다.
  //
  // 형제 모델 `bench`(방석 없는 같은 프레임)는 채택하지 않았다 - 프레임이 동일해 방석 색으로만 갈린다.
  draft({
    id: 'bench-backrest',
    modelId: 'bench-cushion',
    name: '등받이 벤치',
    rotSymmetry: 1,
    sizeClass: 'floor',
    recolorSlot: 'carpet',
    tags: ['hall', 'living', 'bedroom'],
    weight: 4,
    defaultColorId: 7,
  }),

  // 사각 좌판 바스툴. 기존 `stool-bar` 는 육각 좌판이라 위에서 본 모양이 갈린다.
  // rot 4: 좌판이 정사각이고 발받침 가로대가 네 면에 모두 있다 → 90° 돌려도 화면에 아무 일도 없다.
  //        4.2절에 따라 이 소품은 회전 동사에서 제외된다. 점유 칸도 정사각(2×2)이라 규약을 만족한다.
  draft({
    id: 'stool-bar-square',
    modelId: 'stool-bar-square',
    name: '사각 바스툴',
    rotSymmetry: 4,
    sizeClass: 'floor',
    recolorSlot: 'carpet',
    tags: ['kitchen', 'living'],
    weight: 5,
    defaultColorId: 1,
  }),

  /* --- 라운지: 등받이·팔걸이가 있는 1인 좌석. 소파보다 작아 방 가운데 자유롭게 선다 -------------- */

  // 1인 안락의자. 팔걸이가 두꺼워 식탁의자류와 부피부터 갈린다(4×4×4).
  // rot 1: 등받이. sizeClass floor - 1m 남짓이라 시야 차단물은 못 되고 벽에 붙일 이유도 없다.
  // recolor carpet: 천만. 나무 다리를 남기지 않으면 그냥 덩어리가 된다.
  draft({
    id: 'armchair',
    modelId: 'lounge-chair',
    name: '안락의자',
    rotSymmetry: 1,
    sizeClass: 'floor',
    recolorSlot: 'carpet',
    tags: ['living', 'bedroom'],
    weight: 6,
    defaultColorId: 7,
  }),

  // 발받침이 앞으로 뻗은 리클라이너. 깊이가 6칸(1.35m)이라 앞뒤 방향이 노골적으로 보인다.
  // rot 1: 발받침 하나로 네 방향이 전부 구별된다 - 이 그룹에서 회전이 가장 잘 보이는 소품.
  // weight 2: 집에 있어도 한 대다. 흔하게 뿌리면 거실이 가전 매장이 된다.
  draft({
    id: 'recliner',
    modelId: 'lounge-chair-relax',
    name: '리클라이너',
    rotSymmetry: 1,
    sizeClass: 'floor',
    recolorSlot: 'carpet',
    tags: ['living'],
    weight: 2,
    defaultColorId: 1,
  }),

  // 폭 1.46m의 낮은 디자인 라운지체어. 안락의자(0.98m)보다 넓고 등받이가 낮아 옆 실루엣이 갈린다.
  // rot 1: 등받이. recolor carpetBlue - 얇은 금속 프레임을 남겨야 '낮고 넓은' 인상이 유지된다.
  // hall 태그: 복도가 비어 있는 게 현재 문제이고, 등받이 낮은 이 가구는 통행을 덜 막는다.
  draft({
    id: 'lounge-chair-wide',
    modelId: 'lounge-design-chair',
    name: '와이드 라운지체어',
    rotSymmetry: 1,
    sizeClass: 'floor',
    recolorSlot: 'carpetBlue',
    tags: ['living', 'hall'],
    weight: 3,
    defaultColorId: 2,
  }),

  // 등받이 없는 사각 오토만(발받침 겸 스툴).
  // rot 4: 네 면이 같은 정사각 쿠션 덩어리 + 다리 4개 → 90° 돌려도 무변화. 점유 칸도 정사각(4×4).
  //        이 소품에 회전을 허용하면 "서버 diff에는 있는데 화면에는 없는 변화"가 만들어진다.
  draft({
    id: 'ottoman',
    modelId: 'lounge-sofa-ottoman',
    name: '오토만',
    rotSymmetry: 4,
    sizeClass: 'floor',
    recolorSlot: 'carpet',
    tags: ['living', 'bedroom', 'hall'],
    weight: 5,
    defaultColorId: 4,
  }),

  /* --- 소파: 벽에 등을 붙이는 anchor. 1인칭 수색의 시야 차단물이기도 하다 ---------------------- */

  // 한쪽 날개만 등받이 없이 길게 뻗은 카우치(셰이즈). 기존 `sofa`(직선형)와 점유 칸부터 다르다(8×7).
  // rot 1: 셰이즈가 좌우 어느 쪽에 붙었는지가 네 방향 전부에서 보인다.
  draft({
    id: 'sofa-chaise',
    modelId: 'lounge-sofa-long',
    name: '카우치 소파',
    rotSymmetry: 1,
    sizeClass: 'anchor',
    recolorSlot: 'carpet',
    tags: ['living'],
    weight: 3,
    defaultColorId: 7,
  }),

  // ㄱ자 코너 소파. 두 날개 모두 등받이가 있어 카우치와 옆 실루엣이 갈린다.
  // rot 1: 점유 칸이 정사각(8×8)이지만 **형상이 ㄱ자**다 - 90° 돌리면 열린 방향이 바뀌어 확연히 보인다.
  //        정사각 = 회전 무변화가 아니라는 것을 보여 주는 대표 사례라 rotSymmetry 4를 쓰면 안 된다.
  //
  // 형제 모델 `lounge-design-sofa-corner`(2.7m ㄱ자)는 채택하지 않았다.
  // 같은 ㄱ자에 크기만 큰 변종이라, 술래에게는 "코너 소파가 있었다"는 기억 하나로 뭉쳐 읽힌다.
  draft({
    id: 'sofa-corner',
    modelId: 'lounge-sofa-corner',
    name: '코너 소파',
    rotSymmetry: 1,
    sizeClass: 'anchor',
    recolorSlot: 'carpet',
    tags: ['living'],
    weight: 2,
    defaultColorId: 1,
  }),

  // 등받이·팔걸이가 얇은 낮은 직선 소파. 기존 `sofa`(두꺼운 롤 팔걸이)와 두께로 갈리고 폭도 1칸 넓다(9칸).
  // rot 1: 등받이. recolor carpetBlue - 금속 다리를 남긴다.
  draft({
    id: 'sofa-modern',
    modelId: 'lounge-design-sofa',
    name: '모던 소파',
    rotSymmetry: 1,
    sizeClass: 'anchor',
    recolorSlot: 'carpetBlue',
    tags: ['living'],
    weight: 3,
    defaultColorId: 2,
  }),

  /* --- 침실: 침대는 명백한 비대칭이고, 전부 벽에 붙는 anchor다 -------------------------------- */

  // 싱글 침대. 기존 `bed-double`(8칸 폭)의 절반 남짓(5칸)이라 폭만으로 구별된다.
  // rot 1: 머리판과 베개가 한쪽 끝에만 있다 - 180° 회전도 확연하다.
  // recolor carpet: 이불면. 흰 시트(carpetWhite)와 나무 프레임을 남겨야 침구로 읽힌다.
  draft({
    id: 'bed-single',
    modelId: 'bed-single',
    name: '싱글 침대',
    rotSymmetry: 1,
    sizeClass: 'anchor',
    recolorSlot: 'carpet',
    tags: ['bedroom'],
    weight: 5,
    defaultColorId: 9,
  }),

  // 이층침대. 높이 7칸(1.7m)으로 이 그룹에서 유일하게 시야를 완전히 끊는다 -
  // 1인칭 수색이 성립하려면 뒤가 안 보이는 물건이 필요하다(4.1절 대형 구간의 존재 이유).
  // rot 1: 머리판 + 사다리 위치가 방향을 만든다.
  // weight 2: 아파트에 한 대 있을까 말까 한 가구다.
  draft({
    id: 'bed-bunk',
    modelId: 'bed-bunk',
    name: '이층침대',
    rotSymmetry: 1,
    sizeClass: 'anchor',
    recolorSlot: 'carpet',
    tags: ['bedroom'],
    weight: 2,
    defaultColorId: 2,
  }),

  // 다리가 드러난 서랍 협탁. 기존 `nightstand`(모델 cabinet-bed-drawer)는 바닥까지 막힌 통 박스라
  // 다리 사이로 바닥이 보이는 이쪽과 실루엣이 갈린다(점유 칸은 같지만 채운 부피가 다르다).
  // rot 1: 서랍 손잡이가 한 면에만 있다.
  // recolor wood: 천이 없다. 금속 손잡이(metal)는 면적이 너무 작아 색면이 될 수 없다.
  //
  // 형제 모델 `cabinet-bed`(서랍 대신 여닫이문, 역시 통 박스)는 채택하지 않았다 -
  // 기존 `nightstand` 와 같은 닫힌 박스라 손잡이 모양으로만 갈린다.
  draft({
    id: 'nightstand-legs',
    modelId: 'cabinet-bed-drawer-table',
    name: '다리형 협탁',
    rotSymmetry: 1,
    sizeClass: 'floor',
    recolorSlot: 'wood',
    tags: ['bedroom', 'living', 'hall'],
    weight: 6,
    defaultColorId: 5,
  }),

  // 긴 베개(볼스터). 기존 `pillow`(2×2×1 정사각 쿠션)와 길이로 갈린다(4×2×1).
  // rot 2: 양 끝이 같은 직육면체 → 180° 회전은 무변화, 90°는 가로세로가 바뀌어 확연하다.
  // surface: 침대·소파 상판 위에 올라간다. 3절대로 부모-자식이 아니라 y가 높은 셀일 뿐이다.
  //
  // 형제 모델 `pillow-blue`(기존 pillow와 점유 칸까지 동일)와 `pillow-blue-long`(이 엔트리와 형상 동일,
  // 길이만 1칸 김)은 채택하지 않았다 - 둘 다 색으로만 갈리고, 색은 이 게임의 변수다.
  draft({
    id: 'pillow-long',
    modelId: 'pillow-long',
    name: '긴 쿠션',
    rotSymmetry: 2,
    sizeClass: 'surface',
    recolorSlot: 'carpet',
    tags: ['bedroom', 'living', 'hall'],
    weight: 7,
    defaultColorId: 4,
  }),

  /* --- 옷걸이: 이 그룹이 복도에 기여하는 유일한 벽면 소품 -------------------------------------- */

  // 벽걸이 선반형 옷걸이(고리 + 상단 선반). 기존 `coat-rack` 은 서 있는 나무 기둥형(모델 coat-rack-standing)이라
  // 이쪽은 벽에 붙는 납작한 가로 판이고 실루엣이 완전히 다르다.
  // sizeClass wall · tags 4개: 복도가 거의 비어 있는 게 현재 최대 문제이고,
  //   바닥 면적을 안 먹는 벽면 소품은 좁은 복도에 넣을 수 있는 몇 안 되는 후보다.
  // rot 1: 고리가 한쪽 면에만 달렸다 - 벽을 등지지 않으면 즉시 이상해 보인다.
  // recolor wood: 판 전체가 색면. 금속 고리(metalMedium)는 점 단위라 색면이 못 된다.
  draft({
    id: 'coat-hooks',
    modelId: 'coat-rack',
    name: '벽걸이 옷걸이',
    rotSymmetry: 1,
    sizeClass: 'wall',
    recolorSlot: 'wood',
    tags: ['hall', 'bedroom', 'bath', 'living'],
    weight: 6,
    defaultColorId: 5,
  }),
]
